/**
 * Shared coaching-action proposal card for Activity Coach and Ask Your Coach.
 * Displays validated LLM-6 proposals; confirmation uses the existing ask-coach
 * yes/no bridge when conversation continuity is available.
 */
import { useCallback, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  CONFIRM_REPLY,
  dispatchTrainingPlanDraftReady,
  getConfirmationButtonLabel,
  getMaterialActionSuccessMessage,
  getRejectButtonLabel,
  PROPOSAL_PARSE_STATE,
  PROPOSAL_STATUS,
  REJECT_REPLY,
} from "@/lib/coachingActionGuard";

const UI_STATE = {
  IDLE: "IDLE",
  CONFIRMING: "CONFIRMING",
  REJECTING: "REJECTING",
  COMPLETED: "COMPLETED",
  DISMISSED: "DISMISSED",
  ERROR: "ERROR",
};

const formatDuration = (minutes) => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) {
    return null;
  }
  return `${minutes} min`;
};

const PreviewSide = ({ side, label, testIdPrefix }) => {
  if (!side) {
    return null;
  }
  const duration = formatDuration(side.durationMinutes);
  return (
    <div data-testid={`${testIdPrefix}-${label.toLowerCase()}`} className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-dark-text-primary break-words">
        {side.sessionTitle}
      </p>
      {duration && (
        <p className="text-sm text-dark-text-secondary">{duration}</p>
      )}
      <p className="text-sm text-dark-text-secondary break-words">
        {side.difficultyLabel}
      </p>
      {side.intensitySummary && (
        <p className="mt-1 text-xs text-dark-text-muted break-words">
          {side.intensitySummary}
        </p>
      )}
    </div>
  );
};

export const CoachingActionCard = ({
  coachingAction,
  confirmationRequired = false,
  conversationId = null,
  conversationRevision = null,
  materialActionTaken = false,
  planChangePreview = null,
  trainingPlanDraft = null,
  onConfirm,
  onDismiss,
  onReviewTrainingPlanDraft = null,
  testId = "coaching-action-card",
}) => {
  const [uiState, setUiState] = useState(
    materialActionTaken ? UI_STATE.COMPLETED : UI_STATE.IDLE,
  );
  const [actionTaken, setActionTaken] = useState(materialActionTaken);
  const [errorMessage, setErrorMessage] = useState(null);
  const requestInFlightRef = useRef(false);

  const proposal = coachingAction?.parseState === PROPOSAL_PARSE_STATE.VALID
    ? coachingAction.proposal
    : null;

  const isPending = uiState === UI_STATE.CONFIRMING || uiState === UI_STATE.REJECTING;

  const canConfirm = Boolean(
    proposal
    && confirmationRequired
    && conversationId
    && typeof conversationRevision === "number"
    && typeof onConfirm === "function"
    && uiState === UI_STATE.IDLE
    && proposal.status === PROPOSAL_STATUS.READY_FOR_CONFIRMATION,
  );

  const handleDismiss = useCallback(() => {
    if (requestInFlightRef.current) return;
    setUiState(UI_STATE.DISMISSED);
    if (typeof onDismiss === "function") {
      onDismiss();
    }
  }, [onDismiss]);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm || requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setErrorMessage(null);
    setUiState(UI_STATE.CONFIRMING);
    try {
      const response = await onConfirm(CONFIRM_REPLY);
      if (response?.materialActionTaken) {
        setActionTaken(true);
      }
      if (response?.confirmationRequired && !response?.materialActionTaken) {
        setErrorMessage(
          "More confirmation is needed. Reply in the conversation or try again.",
        );
        setUiState(UI_STATE.ERROR);
      } else {
        setUiState(UI_STATE.COMPLETED);
      }
    } catch (err) {
      setUiState(UI_STATE.ERROR);
      setErrorMessage(
        err?.aiCoachError?.message
          || "Could not complete that action. Try again.",
      );
    } finally {
      requestInFlightRef.current = false;
    }
  }, [canConfirm, onConfirm]);

  const handleReject = useCallback(async () => {
    if (!canConfirm || requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setErrorMessage(null);
    setUiState(UI_STATE.REJECTING);
    try {
      await onConfirm(REJECT_REPLY);
      setUiState(UI_STATE.DISMISSED);
    } catch (err) {
      setUiState(UI_STATE.ERROR);
      setErrorMessage(
        err?.aiCoachError?.message
          || "Could not cancel that action. Try again.",
      );
    } finally {
      requestInFlightRef.current = false;
    }
  }, [canConfirm, onConfirm]);

  if (!proposal || uiState === UI_STATE.DISMISSED) {
    return null;
  }

  const successMessage = actionTaken && uiState === UI_STATE.COMPLETED
    ? getMaterialActionSuccessMessage(proposal.intent, true)
    : null;

  const needsInfo = proposal.status === PROPOSAL_STATUS.NEEDS_INFORMATION;
  const showReplanHandoff = proposal.intent === "REPLAN_REMAINING_BLOCK"
    && trainingPlanDraft?.status === "VALIDATED_DRAFT";

  const handleReviewPlan = () => {
    if (typeof onReviewTrainingPlanDraft === "function") {
      onReviewTrainingPlanDraft(trainingPlanDraft);
    } else {
      dispatchTrainingPlanDraftReady(trainingPlanDraft);
    }
    setUiState(UI_STATE.COMPLETED);
  };

  return (
    <aside
      data-testid={testId}
      data-action-intent={proposal.intent}
      aria-label="Coaching action proposal"
      className="mt-4 border border-analytics-amber/40 bg-analytics-amber/5 rounded-lg p-4 w-full min-w-0"
    >
      <p className="text-[11px] uppercase tracking-[0.25em] text-analytics-amber">
        {proposal.intentLabel}
      </p>

      <p
        data-testid={`${testId}-summary`}
        className="mt-2 text-sm text-dark-text-primary whitespace-pre-wrap break-words"
      >
        {proposal.summary}
      </p>

      {proposal.rationale && proposal.rationale !== proposal.summary && (
        <p
          data-testid={`${testId}-rationale`}
          className="mt-2 text-sm text-dark-text-secondary whitespace-pre-wrap break-words"
        >
          {proposal.rationale}
        </p>
      )}

      {planChangePreview?.kind === "single" && (
        <div
          data-testid={`${testId}-plan-preview`}
          className="mt-4 rounded-md border border-dark-border/60 bg-dark-surface/40 p-3 space-y-3"
        >
          <p
            data-testid={`${testId}-preview-day`}
            className="text-sm font-medium text-dark-text-primary"
          >
            {planChangePreview.before.dayLabel}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <PreviewSide
              side={planChangePreview.before}
              label="Before"
              testIdPrefix={`${testId}-preview`}
            />
            <PreviewSide
              side={planChangePreview.after}
              label="After"
              testIdPrefix={`${testId}-preview`}
            />
          </div>
          <div data-testid={`${testId}-preview-why`}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
              Why
            </p>
            <p className="mt-1 text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
              {planChangePreview.why}
            </p>
          </div>
        </div>
      )}

      {planChangePreview?.kind === "ftp" && (
        <div
          data-testid={`${testId}-plan-preview`}
          className="mt-4 rounded-md border border-dark-border/60 bg-dark-surface/40 p-3 space-y-3"
        >
          <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
            FTP update
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div data-testid={`${testId}-preview-ftp-before`}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Current
              </p>
              <p className="mt-1 text-sm text-dark-text-primary">
                {planChangePreview.currentFtpWatts != null
                  ? `${planChangePreview.currentFtpWatts} W`
                  : "Not set"}
                {planChangePreview.currentWkg != null
                  ? ` — ${planChangePreview.currentWkg} W/kg`
                  : ""}
              </p>
            </div>
            <div data-testid={`${testId}-preview-ftp-after`}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Proposed
              </p>
              <p className="mt-1 text-sm text-dark-text-primary">
                {`${planChangePreview.proposedFtpWatts} W`}
                {planChangePreview.proposedWkg != null
                  ? ` — ${planChangePreview.proposedWkg} W/kg`
                  : ""}
              </p>
            </div>
          </div>
          {planChangePreview.changeWatts != null && (
            <p
              data-testid={`${testId}-preview-ftp-change`}
              className="text-sm text-dark-text-secondary"
            >
              Change: {planChangePreview.changeWatts > 0 ? "+" : ""}
              {planChangePreview.changeWatts} W
              {planChangePreview.changePercent != null
                ? ` / ${planChangePreview.changePercent > 0 ? "+" : ""}${planChangePreview.changePercent}%`
                : ""}
            </p>
          )}
          <div data-testid={`${testId}-preview-why`}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
              Why
            </p>
            <p className="mt-1 text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
              {planChangePreview.why}
            </p>
          </div>
          {planChangePreview.evidenceSummary && (
            <div data-testid={`${testId}-preview-evidence`}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Evidence
              </p>
              <p className="mt-1 text-sm text-dark-text-secondary">
                {planChangePreview.evidenceSummary}
              </p>
            </div>
          )}
        </div>
      )}

      {planChangePreview?.kind === "schedule" && (
        <div
          data-testid={`${testId}-plan-preview`}
          className="mt-4 rounded-md border border-dark-border/60 bg-dark-surface/40 p-3 space-y-4"
        >
          {planChangePreview.headline && (
            <p
              data-testid={`${testId}-preview-headline`}
              className="text-sm font-medium text-dark-text-primary"
            >
              {planChangePreview.headline}
            </p>
          )}
          {planChangePreview.availabilityChanges.map((change, index) => (
            <div
              key={`availability-${change.dayLabel}-${index}`}
              data-testid={`${testId}-preview-availability-${index}`}
              className="space-y-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Availability
              </p>
              <p className="text-sm font-medium text-dark-text-primary">{change.dayLabel}</p>
              <p className="text-sm text-dark-text-secondary">
                {change.beforeLabel} → {change.afterLabel}
              </p>
              <p className="text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
                {change.why}
              </p>
            </div>
          ))}
          {planChangePreview.changes.map((change, index) => (
            <div
              key={`plan-${change.dayLabel}-${index}`}
              data-testid={`${testId}-preview-change-${index}`}
              className="space-y-2 border-t border-dark-border/40 pt-3"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                Training plan impact
              </p>
              <p className="text-sm font-medium text-dark-text-primary">{change.dayLabel}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <PreviewSide
                  side={change.before}
                  label="Before"
                  testIdPrefix={`${testId}-preview-change-${index}`}
                />
                <PreviewSide
                  side={change.after}
                  label="After"
                  testIdPrefix={`${testId}-preview-change-${index}`}
                />
              </div>
              <p className="text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
                {change.why}
              </p>
            </div>
          ))}
        </div>
      )}

      {planChangePreview?.kind === "multi" && (
        <div
          data-testid={`${testId}-plan-preview`}
          className="mt-4 rounded-md border border-dark-border/60 bg-dark-surface/40 p-3 space-y-4"
        >
          {planChangePreview.headline && (
            <p
              data-testid={`${testId}-preview-headline`}
              className="text-sm font-medium text-dark-text-primary"
            >
              {planChangePreview.headline}
            </p>
          )}
          {planChangePreview.changes.map((change, index) => (
            <div
              key={`${change.dayLabel}-${index}`}
              data-testid={`${testId}-preview-change-${index}`}
              className="space-y-2 border-t border-dark-border/40 pt-3 first:border-t-0 first:pt-0"
            >
              <p className="text-sm font-medium text-dark-text-primary">
                {change.dayLabel}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <PreviewSide
                  side={change.before}
                  label="Before"
                  testIdPrefix={`${testId}-preview-change-${index}`}
                />
                <PreviewSide
                  side={change.after}
                  label="After"
                  testIdPrefix={`${testId}-preview-change-${index}`}
                />
              </div>
              <p className="text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
                {change.why}
              </p>
            </div>
          ))}
        </div>
      )}

      {!proposal.isKnownIntent && (
        <p
          data-testid={`${testId}-unknown-intent`}
          className="mt-2 text-xs text-dark-text-muted"
        >
          This action type is not fully supported in chat yet.
        </p>
      )}

      {needsInfo && proposal.missingInformation.length > 0 && (
        <ul
          data-testid={`${testId}-missing-info`}
          className="mt-3 space-y-1 text-sm text-dark-text-secondary list-disc pl-4"
        >
          {proposal.missingInformation.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}

      {uiState === UI_STATE.COMPLETED && successMessage && (
        <p
          data-testid={`${testId}-success`}
          className="mt-3 text-sm text-analytics-green"
          role="status"
        >
          {successMessage}
        </p>
      )}

      {uiState === UI_STATE.COMPLETED && !successMessage && (
        <p
          data-testid={`${testId}-acknowledged`}
          className="mt-3 text-sm text-dark-text-muted"
          role="status"
        >
          Acknowledged.
        </p>
      )}

      {errorMessage && (
        <p
          data-testid={`${testId}-error`}
          className="mt-3 text-sm text-analytics-amber"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      {isPending && (
        <p
          data-testid={`${testId}-status`}
          className="mt-3 text-sm text-dark-text-muted flex items-center gap-2"
          role="status"
        >
          <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden="true" />
          {uiState === UI_STATE.CONFIRMING ? "Processing…" : "Cancelling…"}
        </p>
      )}

      {showReplanHandoff && (
        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleReviewPlan}
            data-testid={`${testId}-review-plan`}
            className="btn-primary-app w-full sm:w-auto"
          >
            Review updated plan
          </Button>
        </div>
      )}

      {canConfirm && (
        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid={`${testId}-confirm`}
            className="btn-primary-app w-full sm:w-auto"
          >
            {getConfirmationButtonLabel(proposal.intent)}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isPending}
            onClick={handleReject}
            data-testid={`${testId}-dismiss`}
            className="w-full sm:w-auto"
          >
            {getRejectButtonLabel(proposal.intent)}
          </Button>
        </div>
      )}

      {!canConfirm && uiState === UI_STATE.IDLE && !materialActionTaken && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isPending}
            onClick={handleDismiss}
            data-testid={`${testId}-dismiss`}
            className="w-full sm:w-auto"
          >
            Dismiss
          </Button>
        </div>
      )}
    </aside>
  );
};
